import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import { Link } from "react-router-dom";
import {
  TbStethoscope,
  TbPhoneCall,
  TbMoon,
  TbArmchair,
  TbClipboardList,
  TbCircleCheck,
} from "react-icons/tb";
import styles from "./Consultorios.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

const CONSULTORIOS_FAQ = [
  {
    question: "O chatbot pode dar orientações médicas ou de saúde?",
    answer:
      "Não. O chatbot cuida exclusivamente do agendamento — marcar, confirmar, reagendar e responder dúvidas não-clínicas como horários e convênios aceitos. Orientações médicas são sempre do profissional de saúde.",
  },
  {
    question: "Funciona para médico, dentista e psicólogo?",
    answer:
      "Sim. O fluxo é o de consultórios: antes de marcar, o bot faz a triagem de primeira consulta ou retorno e de particular ou convênio. Você define seus serviços, durações e horários — é isso que ajusta o ritmo ao seu caso.",
  },
  {
    question: "Como o bot lida com convênios?",
    answer:
      "Antes de marcar, ele pergunta se o atendimento é particular ou por convênio e, se for convênio, qual. A resposta fica registrada junto da consulta, então a recepção já recebe o paciente sabendo. Ainda não existe uma lista de convênios aceitos que o bot valide sozinho — a conferência é da recepção.",
  },
  {
    question: "O bot confirma a consulta com o paciente?",
    answer:
      "Sim. Ele envia confirmação automática e lembrete antes da consulta — reduzindo faltas e a receita perdida com cada no-show.",
  },
  {
    question: "Como funciona o cancelamento ou remarcação pelo paciente?",
    answer:
      "O paciente cancela ou remarca direto pelo WhatsApp, sem precisar ligar. O horário liberado fica disponível para outro paciente na lista de espera automaticamente.",
  },
  {
    question: "Posso ter mais de um profissional na mesma conta?",
    answer:
      "Sim, cada profissional com a sua agenda, e o paciente escolhe com quem quer ser atendido. Quantos cabem depende do plano: o Básico inclui 2 e o Profissional, 10.",
  },
];

const CONSULTORIOS_MOCKUP = [
  { type: "out", text: "Posso confirmar minha consulta de amanhã?" },
  {
    type: "in",
    text: "Olá! Sua consulta com a Dra. Ana está marcada para amanhã às 14h. Posso confirmar?",
    buttons: ["Sim, confirmo", "Preciso remarcar"],
  },
  { type: "out", text: "Sim, confirmo" },
  {
    type: "in",
    text: "✅ Confirmado! Lembrete: chegue 10 minutos antes. Te aviso 2h antes também.",
  },
];

const SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Chatbot de WhatsApp para Consultório",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  areaServed: "BR",
  serviceType: "Chatbot de WhatsApp para Consultório",
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: CONSULTORIOS_FAQ.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const DORES = [
  {
    Icon: TbPhoneCall,
    title: "Secretária sobrecarregada",
    text: "Telefone, WhatsApp e paciente presencial ao mesmo tempo — algo sempre fica para depois.",
  },
  {
    Icon: TbMoon,
    title: "Paciente que liga à noite",
    text: "Sábado às 21h o paciente quer marcar. Sem alguém respondendo, vai para o concorrente.",
  },
  {
    Icon: TbArmchair,
    title: "No-show é prejuízo direto",
    text: "Cada consulta não confirmada é receita perdida que poderia ter sido reagendada.",
  },
  {
    Icon: TbClipboardList,
    title: "Triagem manual antes da consulta",
    text: "Primeira vez ou retorno? Convênio ou particular? O bot faz essa triagem sem sua intervenção.",
  },
];

const RECURSOS = [
  "Triagem automática (1ª consulta, retorno, convênio, particular)",
  "Confirmação de consulta e redução de no-show",
  'Lembretes personalizados de preparo ("venha em jejum")',
  "Reagendamento sem atrito via WhatsApp",
  "Lista de espera quando a agenda está cheia",
  "Dúvidas frequentes (horários, endereço, convênios aceitos)",
];

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.CONSULTORIOS,
  business_segment: BUSINESS_SEGMENTS.CONSULTORIOS,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Página de segmento vende AGENDA (o cliente marca hora): o teste que ela
  // oferece é o de Agendamento, e o `?produto=` leva isso ao signup.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Cadastro já carregando o produto — o backend escolhe o plano de trial. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const Consultorios = () => (
  <>
    <SeoHead
      title="Chatbot WhatsApp para Consultório que Reduz Faltas | Nexus Chatbot"
      description="Agendamento automático de consultas pelo WhatsApp. Triagem, confirmação e lembretes sem secretária no telefone. Teste grátis 14 dias."
      canonical="https://nexustech.tec.br/consultorios"
      jsonLd={[SERVICE_LD, FAQ_LD]}
    />

    {/* Hero */}
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <div className={styles.heroCol}>
          <h1 className={styles.heroTitle}>
            <TbStethoscope
              className={styles.titleIcon}
              size={38}
              aria-hidden="true"
            />
            Chatbot de WhatsApp para Consultório que Reduz Faltas e Libera a
            Secretária
          </h1>
          <p className={styles.heroText}>
            Seu paciente agenda, confirma e recebe lembretes de preparo — tudo
            pelo WhatsApp, sem precisar de alguém na linha.
          </p>
          <Link
            to={SIGNUP_PATH}
            className={styles.ctaPrimary}
            onClick={() =>
              trackTrialCtaClick({
                ...CTA_CTX,
                cta_placement: CTA_PLACEMENTS.HERO,
              })
            }
          >
            Testar grátis 14 dias
          </Link>
          <p className={styles.heroNote}>
            Sem cartão. Cancele quando quiser. Configuração em 15 minutos.
          </p>
        </div>
        <div className={styles.heroMockup}>
          <WhatsAppMockup messages={CONSULTORIOS_MOCKUP} looping />
        </div>
      </div>
    </section>

    {/* Dores */}
    <section className={styles.dores}>
      <div className={styles.doresInner}>
        <h2 className={styles.sectionTitle}>
          Por que consultórios usam a Nexus Tech
        </h2>
        <div className={styles.doresGrid}>
          {DORES.map((d, i) => (
            <div key={i} className={styles.doreCard}>
              <d.Icon
                className={styles.doreIcon}
                size={36}
                aria-hidden="true"
              />
              <h3 className={styles.doreTitle}>{d.title}</h3>
              <p className={styles.doreText}>{d.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA intermediário */}
    <section className={styles.ctaMid}>
      <div className={styles.ctaMidInner}>
        <p className={styles.ctaMidText}>
          Pronto para reduzir as faltas e liberar a agenda?
        </p>
        <Link
          to={SIGNUP_PATH}
          className={styles.ctaPrimary}
          onClick={() =>
            trackTrialCtaClick({
              ...CTA_CTX,
              cta_placement: CTA_PLACEMENTS.MIDDLE,
            })
          }
        >
          Testar grátis 14 dias
        </Link>
      </div>
    </section>

    {/* Recursos */}
    <section className={styles.recursos}>
      <div className={styles.recursosInner}>
        <h2 className={styles.sectionTitle}>
          Recursos pensados para consultórios
        </h2>
        <div className={styles.recursosGrid}>
          {RECURSOS.map((r, i) => (
            <div key={i} className={styles.recursoCard}>
              <TbCircleCheck
                className={styles.recursoIcon}
                size={20}
                aria-hidden="true"
              />
              <p className={styles.recursoText}>{r}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className={styles.faq}>
      <div className={styles.faqInner}>
        <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
        <FaqAccordion items={CONSULTORIOS_FAQ} />
      </div>
    </section>

    {/* CTA Final */}
    <section className={styles.ctaFinal}>
      <h2 className={styles.ctaFinalTitle}>
        Menos no-show. Menos telefone. Mais consultas.
      </h2>
      <p className={styles.ctaFinalText}>
        Configure em 15 minutos e comece a receber agendamentos esta semana.
      </p>
      <Link
        to={SIGNUP_PATH}
        className={styles.ctaInverse}
        onClick={() =>
          trackTrialCtaClick({
            ...CTA_CTX,
            cta_placement: CTA_PLACEMENTS.FINAL,
          })
        }
      >
        Testar grátis 14 dias
        <TbStethoscope size={18} aria-hidden="true" />
      </Link>
    </section>
  </>
);

export default Consultorios;
